/**
 * ManagerPanels
 *
 * Agrupa en un solo archivo todos los paneles laterales relacionados
 * con gerentes: visualización (read-only) y creación/edición.
 *
 * Uso en Dashboard:
 *   const managerPanels = useManagerPanels();
 *   ...
 *   managerPanels.openDetail(manager)   // botón "Ver"
 *   managerPanels.openEdit(manager)     // botón "Editar"
 *   managerPanels.openCreate()          // botón "Nuevo Gerente"
 *   ...
 *   <ManagerPanels hook={managerPanels} allCompanies={allCompanies} allBranches={allBranches} onSave={loadData} />
 */

import { useState } from "react";
import { type User } from "@/services/usersService";
import { type Company } from "@/services/companiesService";
import { type Branch } from "@/services/branchesService";
import ManagerDetailModal from "@/components/ManagerDetailModal";
import ManagerFormSheet from "@/components/ManagerFormSheet";

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useManagerPanels() {
  // Panel de detalle (solo lectura)
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [detailManager, setDetailManager] = useState<User | null>(null);

  // Panel de formulario (crear / editar)
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formManager, setFormManager] = useState<User | null>(null);

  const openDetail = (manager: User) => {
    setDetailManager(manager);
    setIsDetailOpen(true);
  };

  const openEdit = (manager: User) => {
    setFormManager(manager);
    setIsFormOpen(true);
  };

  const openCreate = () => {
    setFormManager(null);
    setIsFormOpen(true);
  };

  return {
    // Estado interno — usado por <ManagerPanels />
    _state: { isDetailOpen, detailManager, isFormOpen, formManager },
    _setters: { setIsDetailOpen, setIsFormOpen },
    // Handlers públicos — usados por Dashboard
    openDetail,
    openEdit,
    openCreate,
  };
}

// ─── Componente ──────────────────────────────────────────────────────────────

interface ManagerPanelsProps {
  hook: ReturnType<typeof useManagerPanels>;
  /** Lista de empresas para el selector en el formulario */
  allCompanies: Company[];
  /** Lista de sucursales para filtrar por empresa en el formulario */
  allBranches: Branch[];
  /** Se llama tras crear / editar / cambiar estado para recargar datos */
  onSave: () => void;
}

export function ManagerPanels({
  hook,
  allCompanies,
  allBranches,
  onSave,
}: ManagerPanelsProps) {
  const { _state: s, _setters: set } = hook;

  return (
    <>
      {/* Panel de visualización */}
      <ManagerDetailModal
        isOpen={s.isDetailOpen}
        onClose={() => set.setIsDetailOpen(false)}
        manager={s.detailManager}
      />

      {/* Panel de creación / edición */}
      <ManagerFormSheet
        isOpen={s.isFormOpen}
        onClose={() => set.setIsFormOpen(false)}
        manager={s.formManager}
        allCompanies={allCompanies}
        allBranches={allBranches}
        onSave={onSave}
      />
    </>
  );
}
